import Vue from 'vue'
import { VueReCaptcha } from 'vue-recaptcha-v3'

import { mapGetters, mapActions } from 'vuex'

import MainButton from '@/components/MainButton/index'
import PublicPageWrapper from '@/components/PublicPageWrapper/index'
import ResponseDialog from '@/components/ResponseDialog/index'

import { objectToFormData } from '@/utils/formDataTransformer'
import { getAllCountries } from '@/utils/locationHelpers'

Vue.use(VueReCaptcha, {
  siteKey: process.env.VUE_APP_RECAPTCHA_SITE_KEY
})

export default {
  name: 'Signup',
  components: {
    MainButton,
    PublicPageWrapper,
    ResponseDialog
  },
  data: () => ({
    signupForm: false,
    firstName: '',
    lastName: '',
    country: '',
    username: '',
    emailAddress: '',
    password: '',
    confirmPassword: '',
    firstNameRules: [v => !!v || 'First name is required'],
    lastNameRules: [v => !!v || 'Last name is required'],
    countryRules: [v => !!v || 'Country is required'],
    usernameRules: [
      v => !!v || 'Username is required',
      v => /^\S*$/.test(v) || 'Spaces are not allowed',
      v => v.length >= 5 || 'Username should have minimum of 5 characters',
      v => v.length <= 20 || 'Max characters is 20'
    ],
    emailRules: [
      v => !!v || 'E-mail is required',
      v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'E-mail must be valid'
    ],
    passwordRules: [
      v => !!v || 'Password is required',
      // v =>
      //   /^(?=.*[a-z])/.test(v) ||
      //   'Password must contain at least 1 lowercase letter',
      // v =>
      //   /^(?=.*[A-Z])/.test(v) ||
      //   'Password must contain at least 1 uppercase letter',
      // v => /^(?=.*[0-9])/.test(v) || 'Password must contain at least 1 number',
      v => (v && v.length >= 8) || 'Password must be at least 8 characters'
    ],
    signupResponseDialog: false,
    countries: null,
    selectedFuncName: null,
    dialogMessage: '',
    inviteToken: null,
    countryCode: '',
    continent: '',
    emailCheckerRequest: null,
    emailChecker: true,
    emailAvailable: '',
    usernameCheckerRequest: null,
    usernameChecker: true,
    usernameAvailable: ''
  }),
  computed: {
    ...mapGetters('signup', [
      'REG_LOADING',
      'REG_RESPONSE',
      'INVITE_VERIFICATION_RESPONSE',
      'EMAIL_AVAILABILITY_RESPONSE',
      'USERNAME_AVAILABILITY_RESPONSE'
    ]),
    responseMessage() {
      return this.dialogMessage
    }
  },
  methods: {
    ...mapActions('signup', {
      saveUserAction: 'SAVE_USER',
      verifyInviteAction: 'VERIFY_INVITE',
      checkUsernameAction: 'CHECK_USERNAME_AVAILABILITY',
      checkEmailAction: 'CHECK_EMAIL_AVAILABILITY'
    }),
    ...mapActions({
      cancelAjaxRequestAction: 'CANCEL_AJAX_REQUEST'
    }),
    confirmPasswordRules(v) {
      return v === this.password || `Doesn't match the entered password`
    },
    pickImage() {
      this.$refs.uploadImage.click()
    },
    countryChange(val) {
      if (val) {
        this.countryCode = val.iso3
        this.continent = val.continent
      }
    },
    emailChange(email) {
      if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return true
      }
      this.clearEmailRequest()
      return 'E-mail must be valid'
    },
    usernameChange(username) {
      if (username) {
        if (
          username.length >= 5 &&
          username.length <= 20 &&
          /^\S*$/.test(username)
        ) {
          return true
        } else {
          this.clearUsernameRequest()
          return 'Username must contain at least 5 characters and not more than 20 without spaces'
        }
      }
      this.clearUsernameRequest()
      return 'Username is required'
    },
    clearEmailRequest() {
      clearTimeout(this.emailCheckerRequest)
      this.cancelAjaxRequestAction()
      this.emailAvailable = ''
    },
    clearUsernameRequest() {
      clearTimeout(this.usernameCheckerRequest)
      this.cancelAjaxRequestAction()
      this.usernameAvailable = ''
    },
    emailChangeAPI(val) {
      const {
        srcElement: { value }
      } = val

      if (this.emailChange(value) === true) {
        if (this.emailCheckerRequest) {
          this.clearEmailRequest()
        }

        this.emailCheckerRequest = setTimeout(() => {
          this.checkEmailAction({ email: value })
        }, 500)
      }
    },
    usernameChangeAPI(val) {
      const {
        srcElement: { value }
      } = val

      if (this.usernameChange(value) === true) {
        if (this.usernameCheckerRequest) {
          this.clearUsernameRequest()
        }

        this.usernameCheckerRequest = setTimeout(() => {
          this.checkUsernameAction({
            username: value
          })
        }, 1000)
      }
    },
    async submitRegistration() {
      if (this.$refs.signupForm.validate()) {
        const object = {
          firstName: this.firstName,
          lastName: this.lastName,
          countryCode: this.countryCode,
          continent: this.continent,
          email: this.emailAddress,
          username: this.username,
          password: this.password,
          confirmPassword: this.confirmPassword,
          inviteToken: this.inviteToken
        }

        await this.$recaptchaLoaded()
        const token = await this.$recaptcha('login') // Execute reCAPTCHA with action "login".

        const payload = {
          data: objectToFormData(object),
          config: {
            headers: {
              'content-type': 'multipart/form-data',
              'Authorization': `Recaptcha ${token}`
            }
          }
        }

        this.saveUserAction(payload).then(() => {
          this.signupResponseDialog = true
          this.dialogMessage = this.REG_RESPONSE.message
          this.selectedFuncName = 'afterRegistration'
        })
      }
    },
    afterRegistration() {
      if (this.REG_RESPONSE.success) {
        this.$router.push({ name: 'login' })
      }
    },
    afterVerification() {
      if (this.INVITE_VERIFICATION_RESPONSE) {
        this.$router.push({ path: '/signup' })
      }
    },
    dialogResponseClick() {
      this.signupResponseDialog = false

      if (this.selectedFuncName) {
        this[this.selectedFuncName]()
      }
    }
  },
  watch: {
    EMAIL_AVAILABILITY_RESPONSE(data) {
      if (data.success) {
        this.emailAvailable = data.message
        this.emailChecker = true
      } else {
        this.emailAvailable = ''
        this.emailChecker = data.message
      }
    },
    USERNAME_AVAILABILITY_RESPONSE(data) {
      if (data.success) {
        this.usernameAvailable = data.message
        this.usernameChecker = true
      } else {
        this.usernameAvailable = ''
        this.usernameChecker = data.message
      }
    }
  },
  created() {
    this.countries = getAllCountries()
    this.inviteToken = this.$router.currentRoute.query._i

    if (this.inviteToken) {
      this.verifyInviteAction({ token: this.inviteToken }).then(() => {
        if (this.INVITE_VERIFICATION_RESPONSE.success === false) {
          this.signupResponseDialog = true
          this.dialogMessage = this.INVITE_VERIFICATION_RESPONSE.message
          this.selectedFuncName = 'afterVerification'
          this.inviteToken = ''
        }
      })
    }
  },
  deactivated() {
    this.$refs.signupForm.reset()
  }
}
